/* ------------------------------------------------------------------ */
/*  resolveVariables                                                  */
/* ------------------------------------------------------------------ */
import debugLog from "../../utils/debugLog";
import evaluateVariableConditions from "./evaluateVariableConditions";

/**
 * Build:
 *   1. globalById          { [globalId] : entry }
 *   2. scopedByGlobalId    { [globalId] : scopedEntry }
 *   3. resolvedByGlobalId  { [globalId] : finalString }
 */
export default function resolveVariables({
  globalVariables = [],
  scopedVariables = [],
  manualContext, // { productId, productCategoryIds [], productFeatureIds [] }
}) {
  /* 1 ------------------------------  globals            */
  const globalById = Object.fromEntries(
    globalVariables.map(v => [Number(v.id), v])
  );

  /* 2 ------------------------------  scoped → keyed by *parent* */
  const scopedByGlobalId = Object.fromEntries(
    scopedVariables
      .filter(v => v.variableToInherit?.[0]?.id)
      .map(v => [Number(v.variableToInherit[0].id), v])
  );

  /* 3 ------------------------------  resolved text      */
  const cache = new Map(); // avoid loops

  function resolveOne(id, depth = 0) {
    if (depth > 5) return ""; // safety
    if (cache.has(id)) return cache.get(id);

    const scoped = scopedByGlobalId[id] || null;
    const global = globalById[id];

    debugLog(1, `[variables] ▼ evaluating id=${id}`, {
      scopedExists: !!scoped,
      globalExists: !!global,
    });
    let result = "";

    /* 3‑A ■ try scoped first */
    if (scoped) {
      result = evaluateVariableConditions(scoped, manualContext);
      debugLog(1, `[variables]    scoped check for id=${id} → "${result}"`);
      const empty = !result || result.trim() === "";
      if (empty && scoped.inheritIfEmpty) {
        result = evaluateVariableConditions(global, manualContext);
        debugLog(1, `[variables]    fallback-to-global id=${id} → "${result}"`);
      }
    } else if (global) {
      /* 3‑B ■ otherwise fall back to global */
      result = evaluateVariableConditions(global, manualContext);
      debugLog(1, `[variables]    global only id=${id} → "${result}"`);
    }

    cache.set(id, result);
    if (result === "" || result == null) {
      debugLog(1, `[variables] ▲ id=${id} FINISHED EMPTY`);
    } else {
      debugLog(1, `[variables] ▲ id=${id} FINISHED "${result}"`);
    }
    return result;
  }

  /* 3‑C ■ materialise all once so render stage is O(1)   */
  Object.keys(globalById).forEach(id => resolveOne(Number(id)));

  return {
    globalById,
    scopedByGlobalId,
    resolvedByGlobalId: Object.fromEntries(cache.entries()),
  };
}
